<template>
    <div class="s3-image-upload">
        <div
            v-if="value"
            class="s3-image-upload__preview mb-2"
        >
            <video
                v-if="isVideo"
                :src="value"
                class="img-thumbnail"
                :style="previewStyle"
                controls
                muted
                playsinline
                preload="metadata"
            ></video>
            <img
                v-else
                :src="value"
                alt="preview"
                class="img-thumbnail"
                :style="previewStyle"
            />
            <div v-if="!disabled">
                <base-button
                    size="sm"
                    type="danger"
                    class="mt-1"
                    @click="handleRemove"
                >
                    <i class="fa fa-trash"></i> {{ $t("remove") || "Remove" }}
                </base-button>
            </div>
        </div>

        <div
            v-if="!value"
            class="s3-image-upload__drop border rounded p-3 text-center"
            :class="{ 's3-image-upload__drop--over': isDragOver, 's3-image-upload__drop--disabled': disabled }"
            :aria-disabled="disabled ? 'true' : null"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
            :style="{ cursor: disabled ? 'not-allowed' : 'pointer', background: '#f8f9fa', opacity: disabled ? 0.6 : 1 }"
        >
            <i class="fa fa-cloud-upload-alt fa-2x text-muted mb-2"></i>
            <p class="text-muted small mb-0">{{ hint || $t("app_catalog.image_upload_hint") }}</p>
        </div>

        <input
            ref="fileInput"
            type="file"
            :accept="accept"
            style="display: none;"
            @change="handleFileChange"
        />

        <el-progress
            v-if="uploading"
            :percentage="uploadProgress"
            class="mt-2"
        ></el-progress>

        <div v-if="errorMsg" class="text-danger small mt-1">{{ errorMsg }}</div>
    </div>
</template>

<script>
import { AppCatalogUploadService } from "../../resource";

export default {
    name: "S3ImageUpload",
    props: {
        objectType: {
            type: String,
            required: true
        },
        value: {
            type: String,
            default: ""
        },
        accept: {
            type: String,
            default: "image/jpeg,image/png,image/webp"
        },
        maxSize: {
            type: Number,
            default: 5 * 1024 * 1024
        },
        // Edit pages of the seller catalog (category / product / promotion /
        // bundle) show a bigger, square preview. Off by default so every other
        // caller (storefront settings, home content) keeps its current look.
        large: {
            type: Boolean,
            default: false
        },
        // Optional presign override (Phase 1 ads §6.3): a function
        // ({file_name, content_type, object_type}) → axios promise of
        // {upload_url, cdn_url, object_key}. The LP editor passes
        // LandingPageService.presign (/api/v1/bo/landing-pages/uploads/presign,
        // which also allows video/mp4). Without it the catalog presign is used,
        // exactly as before.
        presign: {
            type: Function,
            default: null
        },
        // Drop-zone text override (the default names JPG/PNG ≤ 5 MB; the LP
        // video slot passes "mp4 ≤ 15 MB").
        hint: {
            type: String,
            default: ""
        },
        // Read-only (e.g. marketing level 1): preview only, no upload / remove.
        disabled: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        // accept="video/mp4" → <video> preview instead of <img>.
        isVideo() {
            return /video\//.test(this.accept || "");
        },
        previewStyle() {
            return this.large
                ? "width: 220px; height: 220px; object-fit: cover; border-radius: 6px;"
                : "max-height: 120px; max-width: 200px; object-fit: cover;";
        }
    },
    data() {
        return {
            uploading: false,
            uploadProgress: 0,
            errorMsg: "",
            isDragOver: false
        };
    },
    methods: {
        triggerFileInput() {
            if (this.disabled) return;
            this.$refs.fileInput.click();
        },
        handleDrop(e) {
            this.isDragOver = false;
            if (this.disabled) return;
            const file = e.dataTransfer.files && e.dataTransfer.files[0];
            if (file) {
                this.uploadFile(file);
            }
        },
        handleFileChange(e) {
            const file = e.target.files && e.target.files[0];
            if (file) {
                this.uploadFile(file);
            }
            this.$refs.fileInput.value = "";
        },
        handleRemove() {
            this.$emit("input", "");
            this.$emit("update:objectKey", "");
        },
        async uploadFile(file) {
            this.errorMsg = "";

            if (file.size > this.maxSize) {
                // The stock message names the 5 MB default; a caller with another
                // limit (LP video, 15 MB) gets one that names its own.
                this.errorMsg = this.maxSize === 5 * 1024 * 1024
                    ? (this.$t("errors.upload.file_too_large") || "File too large (max 5MB)")
                    : this.$t("marketing.lp.err.file_too_large", { mb: Math.round(this.maxSize / (1024 * 1024)) });
                return;
            }

            this.uploading = true;
            this.uploadProgress = 0;

            try {
                const req = {
                    object_type: this.objectType,
                    file_name: file.name,
                    content_type: file.type
                };
                const presignRes = await (this.presign ? this.presign(req) : AppCatalogUploadService.presign(req));

                const { upload_url, cdn_url, object_key } = presignRes.data;

                const xhr = new XMLHttpRequest();
                xhr.upload.addEventListener("progress", (ev) => {
                    if (ev.lengthComputable) {
                        this.uploadProgress = Math.round((ev.loaded / ev.total) * 100);
                    }
                });

                await new Promise((resolve, reject) => {
                    xhr.open("PUT", upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve();
                        } else {
                            reject(new Error("Upload failed: " + xhr.status));
                        }
                    };
                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(file);
                });

                this.$emit("input", cdn_url);
                this.$emit("update:objectKey", object_key);
            } catch (err) {
                const detail = (err && err.message) ? (": " + err.message) : "";
                this.errorMsg = (this.$t("errors.upload.presign_failed") || "Upload failed") + detail;
            } finally {
                this.uploading = false;
            }
        }
    }
};
</script>
